<template>
  <section class="alarms-customer-wrapper">
    <!-- Hero Banner / Page Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-1">
      <div>
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon icon="BellIcon" size="24" class="mr-50" />
          {{ $t('Histórico de Alarmes e Eventos') }}
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Acompanhe em tempo real ou consulte o histórico de alertas gerados pelos seus ativos e grupos.') }}
        </p>
      </div>

      <div class="d-flex align-items-center mt-1 mt-md-0" style="gap: 8px;">
        <b-button
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          @click="showFilters = !showFilters"
          class="d-inline-flex align-items-center"
        >
          <feather-icon icon="FilterIcon" size="14" class="mr-50" />
          <span>{{ showFilters ? $t('Ocultar Filtros') : $t('Filtros') }}</span>
          <b-badge v-if="activeFiltersCount > 0" variant="light-danger" pill class="ml-50">
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-if="itens.length > 0"
          :disabled="isExporting"
          @click="exportAlertsExcel()"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="outline-success"
          size="sm"
          class="d-inline-flex align-items-center"
        >
          <b-spinner v-if="isExporting" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" size="14" class="mr-50" />
          <span class="align-middle">{{ isExporting ? $t('Exportando...') : $t('Exportar Excel') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Informative Tip Banner -->
    <b-alert v-model="showAlertBanner" variant="light-primary" dismissible class="mb-1 border-primary shadow-xs">
      <div class="d-flex align-items-center py-25">
        <b-avatar variant="light-primary" size="40px" class="mr-1 shadow-xs">
          <feather-icon icon="LayersIcon" size="20" class="text-primary" />
        </b-avatar>
        <div>
          <h6 class="mb-25 font-weight-bolder text-primary">
            {{ $t('Configuração Eficiente de Alarmes') }}
          </h6>
          <p class="mb-0 font-small-3 text-muted">
            {{ $t('Para configurar novos alarmes para múltiplos ativos simultaneamente, utilize a gestão de') }}
            <b-link :to="{ name: 'list-group' }" class="font-weight-bold text-primary text-decoration-underline">
              {{ $t('Grupos de Ativos') }}
            </b-link>.
          </p>
        </div>
      </div>
    </b-alert>

    <!-- Advanced Filter Card -->
    <transition name="slide-fade">
      <b-card v-if="showFilters" no-body class="mb-1 shadow-sm border-0 filters-card">
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon icon="SlidersIcon" size="16" class="text-primary mr-50" />
            <h6 class="mb-0 font-weight-bolder">{{ $t('Filtrar Eventos') }}</h6>
            <b-badge v-if="activeFiltersCount > 0" variant="light-primary" pill class="ml-50">
              {{ activeFiltersCount }} {{ $t('aplicados') }}
            </b-badge>
          </div>

          <div class="d-flex align-items-center" style="gap: 6px;">
            <b-button
              v-if="activeFiltersCount > 0"
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              @click="clearFilters"
              v-b-tooltip.hover
              :title="$t('Limpar Filtros')"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              @click="showFilters = false"
              v-b-tooltip.hover
              :title="$t('Fechar')"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <!-- Quick Date Range Chips -->
          <div class="mb-1 d-flex flex-wrap align-items-center" style="gap: 6px;">
            <small class="font-weight-bold text-muted mr-50">{{ $t('Atalhos') }}:</small>
            <b-badge
              href="javascript:void(0)"
              :variant="dateRangePreset === 'today' ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset('today')"
            >
              {{ $t('Hoje') }}
            </b-badge>
            <b-badge
              href="javascript:void(0)"
              :variant="dateRangePreset === 'yesterday' ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset('yesterday')"
            >
              {{ $t('Ontem') }}
            </b-badge>
            <b-badge
              href="javascript:void(0)"
              :variant="dateRangePreset === 'last7' ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset('last7')"
            >
              {{ $t('Últimos 7 dias') }}
            </b-badge>
            <b-badge
              href="javascript:void(0)"
              :variant="dateRangePreset === 'month' ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset('month')"
            >
              {{ $t('Este Mês') }}
            </b-badge>
          </div>

          <b-row>
            <!-- Date Start -->
            <b-col cols="12" md="3" class="mb-1">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Data Inicial') }}</label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon icon="CalendarIcon" size="14" />
                </b-input-group-prepend>
                <flat-pickr v-model="dateStart" class="form-control" :config="flatPickrConfig" :placeholder="$t('Selecione')" />
              </b-input-group>
            </b-col>

            <!-- Date End -->
            <b-col cols="12" md="3" class="mb-1">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Data Final') }}</label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon icon="CalendarIcon" size="14" />
                </b-input-group-prepend>
                <flat-pickr v-model="dateEnd" class="form-control" :config="flatPickrConfig" :placeholder="$t('Selecione')" />
              </b-input-group>
            </b-col>

            <!-- Alarm Types (Multiselect) -->
            <b-col cols="12" md="6" class="mb-1">
              <label class="font-small-2 font-weight-bold mb-25 text-muted">{{ $t('Tipos de Alarme') }}</label>
              <multiselect
                v-model="chosenAlert"
                :options="options"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :placeholder="$t('Todos os tipos de alarme')"
                label="name"
                track-by="id"
                :preselect-first="false"
              >
                <template slot="selection" slot-scope="{ values, isOpen }">
                  <span class="multiselect__single" v-if="values.length && !isOpen">
                    <b-badge variant="light-primary" pill>
                      {{ values.length }} {{ $t('tipo(s) selecionado(s)') }}
                    </b-badge>
                  </span>
                </template>
              </multiselect>
            </b-col>
          </b-row>

          <b-row>
            <!-- Customer Autosuggest -->
            <b-col cols="12" md="3" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="BriefcaseIcon" size="13" class="mr-25 text-primary" />
                {{ $t('Cliente') }}
              </label>
              <div class="position-relative">
                <vue-autosuggest
                  v-model="searchCustomer"
                  :suggestions="filteredOptionsCustomer"
                  :input-props="inputPropsCustomer"
                  :get-suggestion-value="getSuggestionValueCustomer"
                  @selected="selectHandlerCustomer"
                  @input="onInputChangeCustomer"
                >
                  <template slot-scope="{ suggestion }">
                    <div class="d-flex align-items-center py-25 px-50 border-bottom">
                      <feather-icon icon="BriefcaseIcon" size="14" class="mr-50 text-muted flex-shrink-0" />
                      <div class="text-truncate">
                        <span class="font-weight-bold d-block text-truncate">{{ suggestion.item.companyName || suggestion.item.name }}</span>
                        <small v-if="suggestion.item.document" class="text-muted text-truncate d-block">{{ suggestion.item.document }}</small>
                      </div>
                    </div>
                  </template>
                </vue-autosuggest>
                <feather-icon
                  v-if="searchCustomer"
                  icon="XIcon"
                  size="14"
                  class="cursor-pointer position-absolute text-muted"
                  style="right: 10px; top: 10px;"
                  @click="clearCustomerFilter"
                />
              </div>
            </b-col>

            <!-- Asset Autosuggest -->
            <b-col cols="12" md="3" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="TruckIcon" size="13" class="mr-25 text-primary" />
                {{ $t('Ativo / Veículo') }}
              </label>
              <div class="position-relative">
                <vue-autosuggest
                  v-model="searchAsset"
                  :suggestions="filteredOptions"
                  :input-props="inputPropsAsset"
                  :get-suggestion-value="getSuggestionValueAsset"
                  @selected="selectHandler"
                  @input="onInputChange"
                >
                  <template slot-scope="{ suggestion }">
                    <div class="d-flex align-items-center py-25 px-50 border-bottom">
                      <feather-icon icon="TruckIcon" size="14" class="mr-50 text-muted flex-shrink-0" />
                      <div class="text-truncate">
                        <span class="font-weight-bold d-block text-truncate">{{ suggestion.item.plate || suggestion.item.chassisNumber }}</span>
                        <small v-if="suggestion.item.nickname" class="text-muted text-truncate d-block">{{ suggestion.item.nickname }}</small>
                      </div>
                    </div>
                  </template>
                </vue-autosuggest>
                <feather-icon
                  v-if="searchAsset"
                  icon="XIcon"
                  size="14"
                  class="cursor-pointer position-absolute text-muted"
                  style="right: 10px; top: 10px;"
                  @click="clearAssetFilter"
                />
              </div>
            </b-col>

            <!-- Group Autosuggest -->
            <b-col cols="12" md="3" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="LayersIcon" size="13" class="mr-25 text-primary" />
                {{ $t('Grupo de Ativos') }}
              </label>
              <div class="position-relative">
                <vue-autosuggest
                  v-model="searchGroup"
                  :suggestions="filteredOptionsGroup"
                  :input-props="inputPropsGroup"
                  :get-suggestion-value="getSuggestionValueGroup"
                  @selected="selectHandlerGroup"
                  @input="onInputChangeGroup"
                >
                  <template slot-scope="{ suggestion }">
                    <div class="d-flex align-items-center py-25 px-50 border-bottom">
                      <feather-icon icon="LayersIcon" size="14" class="mr-50 text-muted flex-shrink-0" />
                      <div class="text-truncate">
                        <span class="font-weight-bold d-block text-truncate">{{ suggestion.item.name }}</span>
                        <small v-if="suggestion.item.customerName" class="text-muted text-truncate d-block">{{ suggestion.item.customerName }}</small>
                      </div>
                    </div>
                  </template>
                </vue-autosuggest>
                <feather-icon
                  v-if="searchGroup"
                  icon="XIcon"
                  size="14"
                  class="cursor-pointer position-absolute text-muted"
                  style="right: 10px; top: 10px;"
                  @click="clearGroupFilter"
                />
              </div>
            </b-col>

            <!-- Virtual Fence Autosuggest -->
            <b-col cols="12" md="3" class="mb-1 mb-md-0">
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon icon="ShieldIcon" size="13" class="mr-25 text-primary" />
                {{ $t('Cerca Virtual') }}
              </label>
              <div class="position-relative">
                <vue-autosuggest
                  v-model="searchFence"
                  :suggestions="filteredOptionsFence"
                  :input-props="inputPropsFence"
                  :get-suggestion-value="getSuggestionValueFence"
                  @selected="selectHandlerFence"
                  @input="onInputChangeFence"
                >
                  <template slot-scope="{ suggestion }">
                    <div class="d-flex align-items-center py-25 px-50 border-bottom">
                      <feather-icon icon="ShieldIcon" size="14" class="mr-50 text-primary flex-shrink-0" />
                      <div class="text-truncate">
                        <span class="font-weight-bold d-block text-truncate">{{ suggestion.item.name }}</span>
                        <small v-if="suggestion.item.customerName" class="text-muted text-truncate d-block">{{ suggestion.item.customerName }}</small>
                      </div>
                    </div>
                  </template>
                </vue-autosuggest>
                <feather-icon
                  v-if="searchFence"
                  icon="XIcon"
                  size="14"
                  class="cursor-pointer position-absolute text-muted"
                  style="right: 10px; top: 10px;"
                  @click="clearFenceFilter"
                />
              </div>
            </b-col>
          </b-row>

          <!-- Filter Action Bar -->
          <div class="d-flex align-items-center justify-content-between flex-wrap mt-1 pt-75 border-top">
            <div class="text-muted font-small-2 d-flex align-items-center">
              <feather-icon icon="InfoIcon" size="14" class="mr-50 text-primary" />
              <span>
                {{ activeFiltersCount > 0 ? `${activeFiltersCount} ${$t('filtro(s) selecionado(s)')}` : $t('Selecione os filtros desejados e clique em Buscar Eventos.') }}
              </span>
            </div>
            <div class="d-flex align-items-center" style="gap: 8px;">
              <b-button
                v-if="activeFiltersCount > 0"
                variant="outline-secondary"
                size="sm"
                @click="clearFilters"
              >
                {{ $t('Limpar Filtros') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                @click="onSearchClick"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                class="d-inline-flex align-items-center"
              >
                <feather-icon icon="SearchIcon" size="14" class="mr-50" />
                <span>{{ $t('Buscar Eventos') }}</span>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <!-- Main Content Card (Padrão AssetList.vue) -->
    <b-card no-body class="asset-list-card shadow-sm border-0">
      <!-- Active Filters Tag Bar -->
      <div v-if="activeFiltersCount > 0 && !showFilters" class="px-1 py-75 border-bottom bg-light-secondary d-flex flex-wrap align-items-center" style="gap: 6px;">
        <small class="font-weight-bold text-muted mr-50">{{ $t('Filtros ativos') }}:</small>
        <b-badge v-if="dateStart || dateEnd" variant="light-primary" pill class="d-inline-flex align-items-center">
          <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
          {{ formatDateDisplay(dateStart) }} - {{ formatDateDisplay(dateEnd) }}
        </b-badge>
        <b-badge v-if="chosenAlert.length > 0" variant="light-primary" pill class="d-inline-flex align-items-center">
          <feather-icon icon="BellIcon" size="12" class="mr-25" />
          {{ chosenAlert.length }} {{ $t('tipo(s)') }}
        </b-badge>
        <b-badge v-if="ownerId" variant="light-primary" pill class="d-inline-flex align-items-center">
          <feather-icon icon="BriefcaseIcon" size="12" class="mr-25" />
          {{ searchCustomer }}
          <feather-icon icon="XIcon" size="10" class="ml-25 cursor-pointer" @click="clearCustomerFilter" />
        </b-badge>
        <b-badge v-if="assetId" variant="light-primary" pill class="d-inline-flex align-items-center">
          <feather-icon icon="TruckIcon" size="12" class="mr-25" />
          {{ searchAsset }}
          <feather-icon icon="XIcon" size="10" class="ml-25 cursor-pointer" @click="clearAssetFilter" />
        </b-badge>
        <b-badge v-if="groupId" variant="light-primary" pill class="d-inline-flex align-items-center">
          <feather-icon icon="LayersIcon" size="12" class="mr-25" />
          {{ searchGroup }}
          <feather-icon icon="XIcon" size="10" class="ml-25 cursor-pointer" @click="clearGroupFilter" />
        </b-badge>
        <b-badge v-if="virtualFenceId" variant="light-primary" pill class="d-inline-flex align-items-center">
          <feather-icon icon="ShieldIcon" size="12" class="mr-25" />
          {{ searchFence }}
          <feather-icon icon="XIcon" size="10" class="ml-25 cursor-pointer" @click="clearFenceFilter" />
        </b-badge>
        <b-link class="font-small-2 font-weight-bold ml-50 text-danger" @click="clearFilters">
          {{ $t('Limpar todos') }}
        </b-link>
      </div>

      <!-- Table Toolbar (Idêntica ao AssetList / GroupList) -->
      <div class="m-1">
        <b-row class="align-items-center">
          <!-- Per Page Selector -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="mr-50 mb-0 font-small-3 text-muted">{{ $t('Exibir') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mr-50"
              @input="onPerPageChange"
            />
            <label class="mb-0 font-small-3 text-muted">{{ $t('registros') }}</label>
          </b-col>

          <!-- Right Actions -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-md-end justify-content-start" style="gap: 8px;">
            <b-button
              variant="outline-secondary"
              size="sm"
              class="btn-icon"
              @click="fetchAlerts"
              v-b-tooltip.hover
              :title="$t('Atualizar Lista')"
            >
              <feather-icon icon="RotateCwIcon" size="14" :class="{ 'spin-icon': isLoading }" />
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Data Table Component (Vuexy / BootstrapVue) -->
      <b-table
        ref="refAlarmsTable"
        class="position-relative mb-0 asset-data-table table-hover"
        tbody-tr-class="asset-table-row"
        :items="itens"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        :busy="isLoading"
      >
        <!-- Table Busy Skeleton (Inspirado no AssetList.vue) -->
        <template #table-busy>
          <div class="py-1 px-1">
            <div v-for="n in 5" :key="'skeleton-row-' + n" class="p-1 mb-50 rounded bg-light-secondary">
              <b-row class="align-items-center">
                <b-col cols="2"><b-skeleton width="85px" height="18px" animation="wave" /></b-col>
                <b-col cols="2"><b-skeleton width="110px" height="22px" animation="wave" /></b-col>
                <b-col cols="2"><b-skeleton width="95px" height="16px" animation="wave" /></b-col>
                <b-col cols="2"><b-skeleton width="130px" height="24px" animation="wave" /></b-col>
                <b-col cols="2"><b-skeleton width="90px" height="20px" animation="wave" /></b-col>
                <b-col cols="2"><b-skeleton width="120px" height="20px" animation="wave" /></b-col>
              </b-row>
            </div>
          </div>
        </template>

        <!-- Empty State Slot -->
        <template #empty>
          <div class="text-center py-4 px-2">
            <b-avatar variant="light-secondary" size="64px" class="mb-1">
              <feather-icon icon="BellOffIcon" size="32" class="text-muted" />
            </b-avatar>
            <h5 class="font-weight-bolder mb-50">{{ $t('Nenhum evento encontrado') }}</h5>
            <p class="text-muted font-small-3 mb-1 mx-auto" style="max-width: 450px;">
              {{ activeFiltersCount > 0
                ? $t('Não foram encontrados eventos para os filtros e período aplicados. Tente ajustar ou limpar os filtros.')
                : $t('Nenhum alarme foi disparado até o momento para os seus ativos configurados.')
              }}
            </p>
            <b-button
              v-if="activeFiltersCount > 0"
              variant="outline-primary"
              size="sm"
              @click="clearFilters"
              class="d-inline-flex align-items-center"
            >
              <feather-icon icon="RotateCcwIcon" size="14" class="mr-50" />
              <span>{{ $t('Limpar Filtros') }}</span>
            </b-button>
          </div>
        </template>

        <!-- Column: Datetime -->
        <template #cell(datetime)="data">
          <div class="d-inline-flex flex-column">
            <div class="d-flex align-items-center font-weight-bolder text-body font-small-2">
              <feather-icon icon="ClockIcon" size="12" class="mr-25 text-primary flex-shrink-0" />
              <span>{{ formatTimeOnly(data.item.datetime) }}</span>
            </div>
            <small class="text-muted font-small-1">{{ formatDateOnly(data.item.datetime) }}</small>
          </div>
        </template>

        <!-- Column: Asset / Vehicle -->
        <template #cell(asset)="data">
          <div
            v-if="getAssetIdentityParts(data.item).length"
            class="asset-identity"
            v-b-tooltip.hover.top="{ title: getAssetIdentityTooltip(data.item), customClass: 'asset-identity-tooltip', boundary: 'window' }"
          >
            <component
              :is="data.item.assetId ? 'b-link' : 'span'"
              :to="data.item.assetId ? { name: 'apps-asset-summary', params: { id: data.item.assetId } } : undefined"
              class="asset-identity-row"
            >
              <template v-for="(part, index) in getAssetIdentityParts(data.item)">
                <span
                  v-if="index > 0"
                  :key="`sep-${part.type}`"
                  class="asset-id-sep"
                  aria-hidden="true"
                >·</span>
                <span
                  :key="part.type"
                  :class="['asset-id-value', `asset-id-${part.type}`, { 'is-primary': part.primary }]"
                >
                  <span class="sr-only">{{ part.label }}: </span>{{ part.value }}
                </span>
              </template>
            </component>
          </div>
          <span v-else class="text-muted font-small-2">{{ $t('Não identificado') }}</span>
        </template>

        <!-- Column: Customer Name -->
        <template #cell(customerName)="data">
          <div class="d-flex align-items-center text-truncate" :title="data.item.customerName" style="max-width: 200px;">
            <feather-icon icon="BriefcaseIcon" size="14" class="mr-50 text-muted flex-shrink-0" />
            <span class="font-small-3 text-body text-truncate">{{ data.item.customerName || '—' }}</span>
          </div>
        </template>

        <!-- Column: Event Type (Alarm Badges) -->
        <template #cell(eventTypeName)="data">
          <b-badge
            :variant="resolveBadgeVariant(data.item.eventTypeId)"
            class="d-inline-flex align-items-center py-50 px-75 font-small-2 font-weight-bold text-nowrap"
            pill
          >
            <feather-icon :icon="resolveEventIcon(data.item.eventTypeId)" size="13" class="mr-50 flex-shrink-0" />
            <span>{{ data.item.eventTypeName }}</span>
          </b-badge>
        </template>

        <!-- Column: Virtual Fence -->
        <template #cell(virtualFenceName)="data">
          <span
            v-if="data.item.virtualFenceName"
            class="virtual-fence-chip d-inline-flex align-items-center py-25 px-50 rounded font-small-2 font-weight-bold"
            :title="data.item.virtualFenceName"
          >
            <feather-icon icon="ShieldIcon" size="12" class="mr-25 text-primary flex-shrink-0" />
            <span class="text-truncate" style="max-width: 150px;">{{ data.item.virtualFenceName }}</span>
          </span>
          <span v-else class="text-muted font-small-2">
            —
          </span>
        </template>

        <!-- Column: Additional Information (up to 255 chars with UX truncation & hover tooltip) -->
        <template #cell(additionalInfo)="data">
          <div
            v-if="data.item.additionalInfo"
            class="additional-info-chip d-inline-flex align-items-center cursor-pointer"
            v-b-tooltip.hover.top="{ title: data.item.additionalInfo, boundary: 'window' }"
            @click="showAdditionalInfoModal(data.item)"
          >
            <feather-icon icon="InfoIcon" size="12" class="mr-25 text-primary flex-shrink-0" />
            <span class="additional-info-text font-small-2 text-body">
              {{ data.item.additionalInfo }}
            </span>
            <feather-icon
              v-if="data.item.additionalInfo.length > 30"
              icon="Maximize2Icon"
              size="10"
              class="ml-25 text-muted flex-shrink-0 info-expand-icon"
            />
          </div>
          <span v-else class="text-muted font-small-2">
            —
          </span>
        </template>

        <!-- Column: Google Maps -->
        <template #cell(googleMapsLink)="data">
          <b-button
            v-if="getGoogleMapsLink(data.item)"
            v-b-tooltip.hover
            :href="getGoogleMapsLink(data.item)"
            target="_blank"
            rel="noopener noreferrer"
            variant="flat-primary"
            size="sm"
            class="btn-icon maps-link-btn"
            :title="$t('Ver no Google Maps')"
          >
            <feather-icon icon="MapPinIcon" size="16" />
          </b-button>
          <span v-else class="text-muted font-small-2">
            —
          </span>
        </template>
      </b-table>

      <!-- Table Footer: Pagination & Counts (AssetList / GroupList pattern) -->
      <div v-if="totalItems > 0" class="asset-list-footer p-1 border-top d-flex align-items-center justify-content-between flex-wrap" style="gap: 0.75rem;">
        <div class="d-flex align-items-center font-small-3 text-muted">
          <span>
            {{ $t('Exibindo') }} <strong>{{ from }}</strong> {{ $t('a') }} <strong>{{ to }}</strong> {{ $t('de') }} <strong>{{ totalItems }}</strong> {{ $t('eventos') }}
          </span>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalItems"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0 pagination-modern"
          prev-class="prev-item"
          next-class="next-item"
          @input="onPageChange"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </b-card>

    <!-- Modal for Additional Information -->
    <b-modal
      id="modal-additional-info"
      v-model="isModalAdditionalInfoVisible"
      centered
      hide-header
      hide-footer
      size="md"
      modal-class="modal-additional-info-wrapper"
      content-class="border-0 overflow-hidden"
    >
      <div v-if="modalAdditionalInfoItem" class="modal-info-shell">
        <div class="modal-info-header">
          <div class="modal-info-header-glow" />
          <div class="d-flex align-items-start" style="gap: 12px; position: relative; z-index: 1;">
            <div class="modal-info-icon-wrap">
              <feather-icon
                :icon="resolveEventIcon(modalAdditionalInfoItem.eventTypeId)"
                size="20"
                class="text-white"
              />
            </div>
            <div class="modal-info-header-text">
              <p class="mb-25 text-white font-weight-bolder modal-info-title">
                {{ $t('Informações Adicionais') }}
              </p>
              <b-badge
                v-if="modalAdditionalInfoItem.eventTypeName"
                pill
                class="modal-info-event-badge d-inline-flex align-items-center py-25 px-75 font-small-2"
              >
                {{ modalAdditionalInfoItem.eventTypeName }}
              </b-badge>
            </div>
          </div>
          <button
            type="button"
            class="modal-info-close"
            @click="isModalAdditionalInfoVisible = false"
            :aria-label="$t('Fechar')"
            :title="$t('Fechar')"
          >
            <feather-icon icon="XIcon" size="16" />
          </button>
        </div>

        <div class="modal-info-body">
          <div class="modal-info-meta">
            <div class="modal-info-meta-item">
              <feather-icon icon="TruckIcon" size="14" class="text-primary flex-shrink-0" />
              <div class="modal-info-meta-text">
                <span class="modal-info-meta-label">{{ $t('Veículo / Ativo') }}</span>
                <span class="modal-info-meta-value text-truncate">
                  {{ modalAdditionalInfoItem.plate || modalAdditionalInfoItem.chassisNumber || $t('Não identificado') }}
                </span>
              </div>
            </div>
            <div class="modal-info-meta-item">
              <feather-icon icon="ClockIcon" size="14" class="text-primary flex-shrink-0" />
              <div class="modal-info-meta-text">
                <span class="modal-info-meta-label">{{ $t('Data / Horário') }}</span>
                <span class="modal-info-meta-value">
                  {{ formatDateOnly(modalAdditionalInfoItem.datetime) }} · {{ formatTimeOnly(modalAdditionalInfoItem.datetime) }}
                </span>
              </div>
            </div>
            <div v-if="modalAdditionalInfoItem.customerName" class="modal-info-meta-item">
              <feather-icon icon="BriefcaseIcon" size="14" class="text-primary flex-shrink-0" />
              <div class="modal-info-meta-text">
                <span class="modal-info-meta-label">{{ $t('Cliente') }}</span>
                <span class="modal-info-meta-value text-truncate">{{ modalAdditionalInfoItem.customerName }}</span>
              </div>
            </div>
            <div v-if="modalAdditionalInfoItem.virtualFenceName" class="modal-info-meta-item">
              <feather-icon icon="ShieldIcon" size="14" class="text-primary flex-shrink-0" />
              <div class="modal-info-meta-text">
                <span class="modal-info-meta-label">{{ $t('Cerca Virtual') }}</span>
                <span class="modal-info-meta-value text-truncate">{{ modalAdditionalInfoItem.virtualFenceName }}</span>
              </div>
            </div>
            <div v-if="getGoogleMapsLink(modalAdditionalInfoItem)" class="modal-info-meta-item modal-info-meta-maps">
              <feather-icon icon="MapPinIcon" size="14" class="text-danger flex-shrink-0" />
              <div class="modal-info-meta-text">
                <span class="modal-info-meta-label">{{ $t('Localização') }}</span>
                <b-link
                  :href="getGoogleMapsLink(modalAdditionalInfoItem)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="modal-info-maps-link font-small-2 font-weight-bold d-inline-flex align-items-center"
                >
                  <span class="text-truncate">{{ $t('Abrir no Google Maps') }}</span>
                  <feather-icon icon="ExternalLinkIcon" size="11" class="ml-25 flex-shrink-0" />
                </b-link>
              </div>
            </div>
          </div>

          <div class="modal-info-content-box">
            <div class="d-flex align-items-center justify-content-between mb-75">
              <span class="modal-info-content-label d-inline-flex align-items-center">
                <feather-icon icon="FileTextIcon" size="13" class="mr-50" />
                {{ $t('Detalhes do evento') }}
              </span>
              <b-button
                size="sm"
                :variant="additionalInfoCopied ? 'flat-success' : 'flat-primary'"
                class="d-inline-flex align-items-center px-50"
                @click="copyAdditionalInfo"
              >
                <feather-icon :icon="additionalInfoCopied ? 'CheckIcon' : 'CopyIcon'" size="13" class="mr-25" />
                <span>{{ additionalInfoCopied ? $t('Copiado') : $t('Copiar') }}</span>
              </b-button>
            </div>
            <p class="modal-info-text">{{ modalAdditionalInfoItem.additionalInfo }}</p>
          </div>
        </div>

        <div class="modal-info-footer">
          <b-button
            variant="primary"
            class="d-inline-flex align-items-center"
            @click="isModalAdditionalInfoVisible = false"
          >
            <feather-icon icon="CheckIcon" size="14" class="mr-50" />
            {{ $t('Entendi') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from '@vue/composition-api'
import store from '@/store'
import alarmsStoreModule from './alarmsStoreModule'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'

import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BAlert,
  BTable,
  VBToggle,
  VBTooltip,
  BPagination,
  BBadge,
  BAvatar,
  BSpinner,
  BSkeleton,
  BInputGroup,
  BInputGroupPrepend,
  BLink,
  BModal,
} from 'bootstrap-vue'

import flatPickr from 'vue-flatpickr-component'
import Multiselect from 'vue-multiselect'
import { VueAutosuggest } from 'vue-autosuggest'
import vSelect from 'vue-select'
import assetStoreModule from "@/views/main/asset/assetStoreModule";
import managementStoreModule from "@/views/main/management/managementStoreModule";

/** Helper de debounce leve */
const debounce = (fn, delay = 350) => {
  let timeout
  return function(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn.apply(this, args), delay)
  }
}

export default {
  directives: {
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BAlert,
    BTable,
    BBadge,
    BPagination,
    BAvatar,
    BSpinner,
    BSkeleton,
    BInputGroup,
    BInputGroupPrepend,
    BLink,
    BModal,
    flatPickr,
    Multiselect,
    VueAutosuggest,
    vSelect,
  },

  setup(props, { root }) {
    const ALARMS_APP_STORE_MODULE_NAME = 'app-alarms'
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'

    // Register modules
    if (!store.hasModule(ALARMS_APP_STORE_MODULE_NAME)) store.registerModule(ALARMS_APP_STORE_MODULE_NAME, alarmsStoreModule)
    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    if (!store.hasModule('app-asset')) store.registerModule('app-asset', assetStoreModule)

    onUnmounted(() => {
      if (store.hasModule(ALARMS_APP_STORE_MODULE_NAME)) store.unregisterModule(ALARMS_APP_STORE_MODULE_NAME)
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
    })

    const showAlertBanner = ref(true)
    const showFilters = ref(false)
    const isLoading = ref(false)
    const isExporting = ref(false)

    const isModalAdditionalInfoVisible = ref(false)
    const modalAdditionalInfoItem = ref(null)
    const additionalInfoCopied = ref(false)
    let additionalInfoCopyTimeout = null

    const currentPage = ref(1)
    const perPage = ref(10)
    const perPageOptions = [10, 20, 50, 100]
    const itens = ref([])
    const totalItems = ref(0)
    const from = ref(0)
    const to = ref(0)

    const tableColumns = computed(() => [
      { key: 'datetime', label: root.$t('Data / Horário'), thClass: 'col-datetime font-weight-bolder text-uppercase font-small-2' },
      { key: 'asset', label: root.$t('Veículo / Ativo'), thClass: 'col-asset font-weight-bolder text-uppercase font-small-2', tdClass: 'col-asset' },
      { key: 'customerName', label: root.$t('Cliente'), thClass: 'col-customer font-weight-bolder text-uppercase font-small-2' },
      { key: 'eventTypeName', label: root.$t('Tipo de Alarme'), thClass: 'col-event font-weight-bolder text-uppercase font-small-2' },
      { key: 'virtualFenceName', label: root.$t('Cerca Virtual'), thClass: 'col-fence font-weight-bolder text-uppercase font-small-2' },
      { key: 'additionalInfo', label: root.$t('Informações Adicionais'), thClass: 'col-additional-info font-weight-bolder text-uppercase font-small-2' },
      { key: 'googleMapsLink', label: root.$t('Mapa'), thClass: 'col-maps text-center font-weight-bolder text-uppercase font-small-2', tdClass: 'text-center' },
    ])

    const dateStart = ref('')
    const dateEnd = ref('')
    const dateRangePreset = ref('')
    const chosenAlert = ref([])
    const options = ref([])

    const searchAsset = ref('')
    const assetId = ref('')
    const filteredOptions = ref([])

    const searchGroup = ref('')
    const groupId = ref('')
    const filteredOptionsGroup = ref([])

    const searchFence = ref('')
    const virtualFenceId = ref('')
    const filteredOptionsFence = ref([])

    const searchCustomer = ref('')
    const ownerId = ref('')
    const filteredOptionsCustomer = ref([])

    const inputPropsAsset = computed(() => ({
      id: 'autosuggest__asset_input',
      class: 'form-control form-control-sm',
      placeholder: root.$t('Placa ou chassi...'),
    }))

    const inputPropsGroup = computed(() => ({
      id: 'autosuggest__group_input',
      class: 'form-control form-control-sm',
      placeholder: root.$t('Nome do grupo...'),
    }))

    const inputPropsFence = computed(() => ({
      id: 'autosuggest__fence_input',
      class: 'form-control form-control-sm',
      placeholder: root.$t('Nome da cerca virtual...'),
    }))

    const inputPropsCustomer = computed(() => ({
      id: 'autosuggest__customer_input',
      class: 'form-control form-control-sm',
      placeholder: root.$t('Nome do cliente...'),
    }))

    const getSuggestionValueAsset = (suggestion) => {
      if (!suggestion) return ''
      if (suggestion.item) return suggestion.item.plate || suggestion.item.chassisNumber || ''
      return typeof suggestion === 'string' ? suggestion : ''
    }

    const getSuggestionValueGroup = (suggestion) => {
      if (!suggestion) return ''
      if (suggestion.item) return suggestion.item.name || ''
      return typeof suggestion === 'string' ? suggestion : ''
    }

    const getSuggestionValueFence = (suggestion) => {
      if (!suggestion) return ''
      if (suggestion.item) return suggestion.item.name || ''
      return typeof suggestion === 'string' ? suggestion : ''
    }

    const getSuggestionValueCustomer = (suggestion) => {
      if (!suggestion) return ''
      if (suggestion.item) return suggestion.item.companyName || suggestion.item.name || ''
      return typeof suggestion === 'string' ? suggestion : ''
    }

    const flatPickrConfig = ref({
      altFormat: 'd/m/Y',
      altInput: true,
      dateFormat: 'Y-m-d',
      locale: getFlatpickrLocale(),
    })

    const activeFiltersCount = computed(() => {
      let count = 0
      if (dateStart.value || dateEnd.value) count++
      if (chosenAlert.value && chosenAlert.value.length > 0) count++
      if (assetId.value) count++
      if (groupId.value) count++
      if (virtualFenceId.value) count++
      if (ownerId.value) count++
      return count
    })

    const setDatePreset = (preset) => {
      dateRangePreset.value = preset
      const now = new Date()
      const formatYMD = (d) => {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
      }

      if (preset === 'today') {
        const todayStr = formatYMD(now)
        dateStart.value = todayStr
        dateEnd.value = todayStr
      } else if (preset === 'yesterday') {
        const yest = new Date()
        yest.setDate(now.getDate() - 1)
        const yestStr = formatYMD(yest)
        dateStart.value = yestStr
        dateEnd.value = yestStr
      } else if (preset === 'last7') {
        const past = new Date()
        past.setDate(now.getDate() - 7)
        dateStart.value = formatYMD(past)
        dateEnd.value = formatYMD(now)
      } else if (preset === 'month') {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        dateStart.value = formatYMD(startOfMonth)
        dateEnd.value = formatYMD(now)
      }
    }

    const clearFilters = () => {
      dateStart.value = ''
      dateEnd.value = ''
      dateRangePreset.value = ''
      chosenAlert.value = []
      searchAsset.value = ''
      assetId.value = ''
      searchGroup.value = ''
      groupId.value = ''
      searchFence.value = ''
      virtualFenceId.value = ''
      filteredOptionsFence.value = []
      searchCustomer.value = ''
      ownerId.value = ''
      filteredOptionsCustomer.value = []
      syncAssetQueryFromState()
      currentPage.value = 1
      fetchAlerts()
    }

    const clearAssetFilter = () => {
      const hadFilter = Boolean(assetId.value)
      searchAsset.value = ''
      assetId.value = ''
      filteredOptions.value = []
      syncAssetQueryFromState()
      if (hadFilter) {
        currentPage.value = 1
        fetchAlerts()
      }
    }

    const clearGroupFilter = () => {
      searchGroup.value = ''
      groupId.value = ''
      filteredOptionsGroup.value = []
    }

    const clearFenceFilter = () => {
      searchFence.value = ''
      virtualFenceId.value = ''
      filteredOptionsFence.value = []
    }

    const clearCustomerFilter = () => {
      searchCustomer.value = ''
      ownerId.value = ''
      filteredOptionsCustomer.value = []
    }

    const msgDialog = (msg, icon) => {
      root.$swal({
        position: 'top-end',
        icon: icon,
        title: msg,
        showConfirmButton: false,
        timer: 3000,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
    }

    const _fetchGroupSuggestions = debounce((text) => {
      store.dispatch(`${MANAGEMENT_APP_STORE_MODULE_NAME}/fetchAllGroup`, {
        q: text,
        page: 1,
        perPage: 10
      })
        .then(response => {
          filteredOptionsGroup.value = [{
            data: response.data.data || [],
          }]
        })
        .catch(() => {
          filteredOptionsGroup.value = []
        })
    }, 350)

    const onInputChangeGroup = (text) => {
      if (text === null || text === undefined) return
      if (typeof text === 'object' || text === '[object Object]') {
        clearGroupFilter()
        return
      }

      searchGroup.value = text

      if (!text || text.length < 2) {
        groupId.value = ''
        filteredOptionsGroup.value = []
        return
      }

      _fetchGroupSuggestions(text)
    }

    const selectHandlerGroup = (option) => {
      if (option && option.item) {
        filteredOptionsGroup.value = []
        searchGroup.value = option.item.name
        groupId.value = option.item.id
      }
    }

    const _fetchAssetSuggestions = debounce((text) => {
      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchAssets`, {
        q: text,
        page: 1,
        perPage: 10
      })
        .then(response => {
          filteredOptions.value = [{
            data: response.data.data || [],
          }]
        })
        .catch(() => {
          filteredOptions.value = []
        })
    }, 350)

    const onInputChange = (text) => {
      if (text === null || text === undefined) return
      if (typeof text === 'object' || text === '[object Object]') {
        clearAssetFilter()
        return
      }

      searchAsset.value = text

      if (!text || text.length < 2) {
        assetId.value = ''
        filteredOptions.value = []
        return
      }

      _fetchAssetSuggestions(text)
    }

    const selectHandler = (option) => {
      if (option && option.item) {
        filteredOptions.value = []
        searchAsset.value = option.item.plate || option.item.chassisNumber || option.item.nickname || option.item.nickName || ''
        assetId.value = option.item.id
        syncAssetQueryFromState()
      }
    }

    const _fetchFenceSuggestions = debounce((text) => {
      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchFences`, {
        q: text,
        page: 1,
        perPage: 10
      })
        .then(response => {
          filteredOptionsFence.value = [{
            data: response.data.data || [],
          }]
        })
        .catch(() => {
          filteredOptionsFence.value = []
        })
    }, 350)

    const onInputChangeFence = (text) => {
      if (text === null || text === undefined) return
      if (typeof text === 'object' || text === '[object Object]') {
        clearFenceFilter()
        return
      }

      searchFence.value = text

      if (!text || text.length < 2) {
        virtualFenceId.value = ''
        filteredOptionsFence.value = []
        return
      }

      _fetchFenceSuggestions(text)
    }

    const selectHandlerFence = (option) => {
      if (option && option.item) {
        filteredOptionsFence.value = []
        searchFence.value = option.item.name
        virtualFenceId.value = option.item.id
      }
    }

    const _fetchCustomerSuggestions = debounce((text) => {
      store.dispatch(`${MANAGEMENT_APP_STORE_MODULE_NAME}/fetchCustomers`, {
        q: text
      })
        .then(response => {
          filteredOptionsCustomer.value = [{
            data: response.data.data || [],
          }]
        })
        .catch(() => {
          filteredOptionsCustomer.value = []
        })
    }, 350)

    const onInputChangeCustomer = (text) => {
      if (text === null || text === undefined) return
      if (typeof text === 'object' || text === '[object Object]') {
        clearCustomerFilter()
        return
      }

      searchCustomer.value = text

      if (!text || text.length < 2) {
        ownerId.value = ''
        filteredOptionsCustomer.value = []
        return
      }

      _fetchCustomerSuggestions(text)
    }

    const selectHandlerCustomer = (option) => {
      if (option && option.item) {
        filteredOptionsCustomer.value = []
        searchCustomer.value = option.item.companyName || option.item.name || ''
        ownerId.value = option.item.id
      }
    }

    const resolveBadgeVariant = (typeId) => {
      switch (typeId) {
        case 1: // Ignition ON
          return 'light-success'
        case 2: // Ignition OFF
          return 'light-secondary'
        case 5: // Move violation
        case 8: // Overspeed
        case 14: // Severe use
        case 17: // Failure code
        case 19: // Coolant temp
        case 20: // Hydraulic temp
        case 21: // Transmission temp
          return 'light-danger'
        case 9: // Fence enter
        case 10: // Fence leave
        case 22: // Fence stay in
        case 23: // Fence stay out
          return 'light-primary'
        case 11: // Fence speed violation
          return 'light-danger'
        case 12: // Low fuel
        case 13: // High consumption
        case 18: // Maintenance warning
          return 'light-warning'
        case 15: // Idle use
          return 'light-info'
        case 16: // Long time without connection
          return 'light-secondary'
        default:
          return 'light-warning'
      }
    }

    const resolveEventIcon = (typeId) => {
      switch (typeId) {
        case 1:
        case 2:
          return 'PowerIcon'
        case 5:
          return 'AlertTriangleIcon'
        case 8:
          return 'TrendingUpIcon'
        case 9:
        case 10:
        case 11:
        case 22:
        case 23:
          return 'ShieldIcon'
        case 12:
        case 13:
          return 'DropletIcon'
        case 14:
          return 'AlertOctagonIcon'
        case 15:
          return 'ClockIcon'
        case 16:
          return 'WifiOffIcon'
        case 17:
          return 'AlertCircleIcon'
        case 18:
          return 'ToolIcon'
        case 19:
        case 20:
        case 21:
          return 'ThermometerIcon'
        default:
          return 'BellIcon'
      }
    }

    const fetchTypeAlerts = () => {
      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchTypeAlerts`)
        .then(response => {
          const rawData = response.data.data || []
          const excludedIds = [3, 4, 6, 7]
          options.value = rawData.filter(item => !excludedIds.includes(item.id))
        })
        .catch(() => {})
    }

    const formateDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleTimeString(navigator.language, {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const formatDateDisplay = (dateStr) => {
      if (!dateStr) return ''
      const parts = dateStr.split('-')
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`
      return dateStr
    }

    const setHour = (date) => {
      const dt = new Date(date).toISOString()
      return `${dt.split('T')[0]}T23:59:59.${dt.split('.')[1]}`
    }

    const alertToIds = (chosenAlertParam) => {
      return (chosenAlertParam || []).map(item => item.id)
    }

    const formatDateOnly = (dateStr) => {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(getIntlLocale())
    }

    const formatTimeOnly = (dateStr) => {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleTimeString(navigator.language, {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const fetchAlerts = () => {
      const start = dateStart.value ? new Date(dateStart.value).toISOString() : ''
      const end = dateEnd.value ? setHour(dateEnd.value) : ''
      const alertIds = chosenAlert.value.length > 0 ? alertToIds(chosenAlert.value) : []

      isLoading.value = true

      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchAlerts`, {
        q: '',
        page: currentPage.value,
        perPage: perPage.value,
        assetId: assetId.value,
        groupId: groupId.value,
        virtualFenceId: virtualFenceId.value,
        ownerId: ownerId.value,
        alertIds: alertIds,
        start: start,
        end: end
      })
        .then(response => {
          const rawData = response.data.data || []
          itens.value = rawData
          totalItems.value = response.data.totalItems || 0

          const localItemsCount = rawData.length
          const currentPerPage = perPage.value
          from.value = totalItems.value === 0 ? 0 : currentPerPage * (currentPage.value - 1) + (localItemsCount ? 1 : 0)
          to.value = currentPerPage * (currentPage.value - 1) + localItemsCount
        })
        .catch(() => {})
        .finally(() => {
          isLoading.value = false
        })
    }

    const exportAlertsExcel = () => {
      const start = dateStart.value ? new Date(dateStart.value).toISOString() : ''
      const end = dateEnd.value ? setHour(dateEnd.value) : ''
      const alertIds = chosenAlert.value.length > 0 ? alertToIds(chosenAlert.value) : []

      isExporting.value = true

      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/exportAlertsExcel`, {
        q: '',
        page: currentPage.value,
        perPage: perPage.value,
        assetId: assetId.value,
        groupId: groupId.value,
        virtualFenceId: virtualFenceId.value,
        ownerId: ownerId.value,
        alertIds: alertIds,
        start: start,
        end: end
      })
        .then(response => {
          const date = new Date().toISOString().slice(0, 10)
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `historico_alarmes_${date}.xlsx`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          msgDialog(root.$t('Planilha exportada com sucesso!'), 'success')
        })
        .catch(() => {
          msgDialog(root.$t('Erro ao exportar arquivo Excel'), 'error')
        })
        .finally(() => {
          isExporting.value = false
        })
    }

    const getGoogleMapsLink = (item) => {
      if (!item) return ''
      const link = item.googleMapsLink || item.GoogleMapsLink
      if (typeof link === 'string' && link.trim()) return link.trim()
      const lat = item.latitude !== undefined && item.latitude !== null ? item.latitude : item.lat
      const lng = item.longitude !== undefined && item.longitude !== null ? item.longitude : item.lng
      if (lat !== undefined && lat !== null && lng !== undefined && lng !== null && lat !== '' && lng !== '') {
        return `https://www.google.com/maps?q=${lat},${lng}`
      }
      return ''
    }

    const getAssetNickname = (item) => {
      if (!item) return ''
      return String(item.nickname || item.nickName || '').trim()
    }

    const getAssetIdentityParts = (item) => {
      if (!item) return []

      const plate = item.plate ? String(item.plate).trim() : ''
      const chassis = String(item.chassisNumber || item.chassis || '').trim()
      const nickname = getAssetNickname(item)
      const parts = []

      if (plate) {
        parts.push({
          type: 'plate',
          value: plate,
          label: root.$t('Placa'),
          primary: true,
        })
      }

      if (chassis) {
        parts.push({
          type: 'chassis',
          value: chassis,
          label: root.$t('Chassi'),
          primary: !plate,
        })
      }

      if (nickname) {
        parts.push({
          type: 'nickname',
          value: nickname,
          label: root.$t('Apelido'),
          primary: !plate && !chassis,
        })
      }

      return parts
    }

    const getAssetIdentityTooltip = (item) => {
      const parts = getAssetIdentityParts(item)
      if (!parts.length) return root.$t('Não identificado')

      const lines = parts.map(part => `${part.label}: ${part.value}`)
      if (item && item.assetId) {
        lines.push(root.$t('Clique para ver detalhes do ativo'))
      }
      return lines.join('\n')
    }

    const showAdditionalInfoModal = (item) => {
      if (!item || !item.additionalInfo) return
      additionalInfoCopied.value = false
      if (additionalInfoCopyTimeout) {
        clearTimeout(additionalInfoCopyTimeout)
        additionalInfoCopyTimeout = null
      }
      modalAdditionalInfoItem.value = item
      isModalAdditionalInfoVisible.value = true
    }

    const copyAdditionalInfo = () => {
      const text = modalAdditionalInfoItem.value && modalAdditionalInfoItem.value.additionalInfo
      if (!text) return

      const markCopied = () => {
        additionalInfoCopied.value = true
        if (additionalInfoCopyTimeout) clearTimeout(additionalInfoCopyTimeout)
        additionalInfoCopyTimeout = setTimeout(() => {
          additionalInfoCopied.value = false
          additionalInfoCopyTimeout = null
        }, 2000)
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(markCopied).catch(() => {
          msgDialog(root.$t('Não foi possível copiar o texto'), 'error')
        })
        return
      }

      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'absolute'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        markCopied()
      } catch (e) {
        msgDialog(root.$t('Não foi possível copiar o texto'), 'error')
      }
      document.body.removeChild(textarea)
    }

    const onPerPageChange = () => {
      currentPage.value = 1
      fetchAlerts()
    }

    const onPageChange = () => {
      fetchAlerts()
    }

    const onSearchClick = () => {
      currentPage.value = 1
      fetchAlerts()
    }

    const normalizeRouteParam = (value) => {
      if (Array.isArray(value)) return value[0] ? String(value[0]) : ''
      return value ? String(value) : ''
    }

    let isSyncingAssetQuery = false

    const syncAssetQueryFromState = () => {
      const currentId = normalizeRouteParam(root.$route.query.assetId)
      const currentLabel = normalizeRouteParam(root.$route.query.assetLabel)
      const nextId = assetId.value ? String(assetId.value) : ''
      const nextLabel = searchAsset.value ? String(searchAsset.value) : ''

      if (currentId === nextId && currentLabel === nextLabel) return

      const query = { ...root.$route.query }
      if (nextId) {
        query.assetId = nextId
        if (nextLabel) query.assetLabel = nextLabel
        else delete query.assetLabel
      } else {
        delete query.assetId
        delete query.assetLabel
      }

      isSyncingAssetQuery = true
      root.$router.replace({ query }).catch(() => {})
      nextTick(() => {
        isSyncingAssetQuery = false
      })
    }

    const resolveAssetLabel = (id) => {
      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchAsset`, { id })
        .then(response => {
          if (String(assetId.value) !== String(id)) return
          const asset = (response.data && response.data.data) || response.data || {}
          const label = asset.plate || asset.chassisNumber || asset.nickname || asset.nickName || ''
          if (label) {
            searchAsset.value = label
            syncAssetQueryFromState()
          }
        })
        .catch(() => {})
    }

    const applyAssetFilterFromRoute = () => {
      const idFromQuery = normalizeRouteParam(root.$route.query.assetId)
      if (!idFromQuery) {
        if (assetId.value) {
          assetId.value = ''
          searchAsset.value = ''
          filteredOptions.value = []
        }
        return false
      }

      assetId.value = idFromQuery
      const labelFromQuery = normalizeRouteParam(root.$route.query.assetLabel)
      if (labelFromQuery) {
        searchAsset.value = labelFromQuery
      } else if (!searchAsset.value) {
        searchAsset.value = root.$t('Ativo')
        resolveAssetLabel(idFromQuery)
      }

      return true
    }

    watch(
      () => root.$route.query.assetId,
      (newId, oldId) => {
        if (isSyncingAssetQuery) return
        if (normalizeRouteParam(newId) === normalizeRouteParam(oldId)) return
        applyAssetFilterFromRoute()
        currentPage.value = 1
        fetchAlerts()
      }
    )

    onMounted(() => {
      fetchTypeAlerts()
      applyAssetFilterFromRoute()
      fetchAlerts()
    })

    return {
      showAlertBanner,
      showFilters,
      isLoading,
      isExporting,
      currentPage,
      perPage,
      perPageOptions,
      itens,
      totalItems,
      tableColumns,
      from,
      to,
      dateStart,
      dateEnd,
      dateRangePreset,
      chosenAlert,
      options,
      searchAsset,
      assetId,
      filteredOptions,
      searchGroup,
      groupId,
      filteredOptionsGroup,
      searchFence,
      virtualFenceId,
      filteredOptionsFence,
      inputPropsAsset,
      inputPropsGroup,
      inputPropsFence,
      getSuggestionValueFence,
      getSuggestionValueAsset,
      getSuggestionValueGroup,
      selectHandlerFence,
      onInputChangeFence,
      clearFenceFilter,
      searchCustomer,
      ownerId,
      filteredOptionsCustomer,
      inputPropsCustomer,
      getSuggestionValueCustomer,
      onInputChangeCustomer,
      selectHandlerCustomer,
      clearCustomerFilter,
      flatPickrConfig,
      activeFiltersCount,
      setDatePreset,
      clearFilters,
      clearAssetFilter,
      clearGroupFilter,
      onInputChangeGroup,
      selectHandlerGroup,
      selectHandler,
      onInputChange,
      resolveBadgeVariant,
      resolveEventIcon,
      formatDateOnly,
      formatTimeOnly,
      formateDate: formatTimeOnly,
      formatDateDisplay,
      exportAlertsExcel,
      fetchAlerts,
      onSearchClick,
      onPerPageChange,
      onPageChange,
      getGoogleMapsLink,
      getAssetIdentityParts,
      getAssetIdentityTooltip,
      showAdditionalInfoModal,
      copyAdditionalInfo,
      isModalAdditionalInfoVisible,
      modalAdditionalInfoItem,
      additionalInfoCopied,
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.modal-additional-info-wrapper {
  .modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(115, 103, 240, 0.14) !important;
    padding: 0 !important;
  }

  .modal-body {
    padding: 0 !important;
    margin: 0 !important;
  }
}

.tooltip.asset-identity-tooltip .tooltip-inner {
  white-space: pre-line;
  text-align: left;
  max-width: 280px;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.alarms-customer-wrapper {
  .filters-card {
    border-radius: 0.5rem;
  }
}

// Per Page Selector
.per-page-selector {
  width: 84px;
}

// Spin Icon Animation
.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.asset-identity {
  max-width: 280px;
  min-width: 0;
}

.asset-identity-row {
  display: flex;
  align-items: baseline;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
}

a.asset-identity-row:hover {
  text-decoration: none;

  .asset-id-plate,
  .asset-id-chassis.is-primary {
    color: #7367f0;
  }

  .asset-id-nickname {
    color: #5e50ee;
  }
}

.asset-id-sep {
  flex: 0 0 auto;
  margin: 0 0.35rem;
  color: #c7c5d1;
  font-size: 0.7rem;
  line-height: 1;
}

.asset-id-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.25;
}

.asset-id-plate {
  flex: 0 0 auto;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5e5873;
}

.asset-id-chassis {
  flex: 0 1 auto;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #82868b;
  font-variant-numeric: tabular-nums;

  &.is-primary {
    font-size: 0.8rem;
    font-weight: 700;
    color: #5e5873;
  }
}

.asset-id-nickname {
  flex: 1 1 auto;
  font-size: 0.78rem;
  font-weight: 500;
  font-style: italic;
  color: #7367f0;

  &.is-primary {
    flex: 0 1 auto;
    font-weight: 600;
  }
}

// Data Table Styling (Padrão AssetList.vue)
.asset-data-table {
  ::v-deep tbody tr.asset-table-row {
    transition: background-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      background-color: rgba(115, 103, 240, 0.04) !important;
      box-shadow: inset 3px 0 0 #7367f0;
    }
  }

  ::v-deep tr.b-table-busy-slot {
    > td {
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
    }
  }

  ::v-deep {
    th.col-datetime, td.col-datetime { min-width: 130px; width: 140px; }
    th.col-asset, td.col-asset { min-width: 220px; max-width: 280px; white-space: nowrap; }
    th.col-customer, td.col-customer { min-width: 140px; }
    th.col-event, td.col-event { min-width: 160px; }
    th.col-fence, td.col-fence { min-width: 140px; }
    th.col-additional-info, td.col-additional-info { min-width: 200px; max-width: 280px; }
    th.col-maps, td.col-maps { min-width: 72px; width: 88px; text-align: center; }
  }
}

// Virtual Fence Chip
.virtual-fence-chip {
  background: rgba(115, 103, 240, 0.08);
  border: 1px solid rgba(115, 103, 240, 0.18);
  color: #7367f0;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(115, 103, 240, 0.14);
    border-color: #7367f0;
  }
}

// Additional Info Chip (Up to 255 chars with truncation, tooltip & modal)
.additional-info-chip {
  max-width: 250px;
  background-color: #f8f8fa;
  border: 1px solid #ebe9f1;
  border-radius: 5px;
  padding: 3px 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: #f1f0f8;
    border-color: #7367f0;
    box-shadow: 0 2px 6px rgba(115, 103, 240, 0.12);

    .info-expand-icon {
      color: #7367f0 !important;
    }
  }

  .additional-info-text {
    display: inline-block;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
  }
}

.maps-link-btn {
  color: #7367f0;
  transition: all 0.15s ease;

  &:hover {
    color: #5e50ee;
    transform: translateY(-1px);
  }
}

// Shimmer wave refinement for Bootstrap BSkeleton
::v-deep .b-skeleton {
  background-color: rgba(234, 236, 241, 0.7) !important;
  border-radius: 4px;

  &.b-skeleton-animate-wave {
    background: linear-gradient(
      90deg,
      rgba(234, 236, 241, 0.7) 0%,
      rgba(250, 251, 253, 0.95) 50%,
      rgba(234, 236, 241, 0.7) 100%
    ) !important;
    background-size: 200% 100% !important;
    animation-duration: 1.4s !important;
  }
}

// Footer / Pagination (Padrão AssetList.vue)
.asset-list-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.pagination-modern {
  ::v-deep .page-item {
    &.active .page-link {
      border-radius: 7px;
      background: #7367f0;
      border-color: #7367f0;
      box-shadow: 0 3px 10px rgba(115, 103, 240, 0.4);
    }

    .page-link {
      border-radius: 7px;
      font-weight: 500;
      transition: all 0.15s ease;

      &:hover {
        background: rgba(115, 103, 240, 0.08);
        color: #7367f0;
      }
    }
  }
}

// Dark Mode Support
.dark-layout {
  .asset-data-table {
    ::v-deep tbody tr.asset-table-row:hover {
      background-color: rgba(115, 103, 240, 0.1) !important;
      box-shadow: inset 3px 0 0 #7367f0;
    }
  }

  .asset-id-plate,
  .asset-id-chassis.is-primary {
    color: #d0d2d6;
  }

  .asset-id-chassis {
    color: #b4b7bd;
  }

  .asset-id-nickname {
    color: #a8a3f8;
  }

  .asset-id-sep {
    color: #676d7d;
  }

  a.asset-identity-row:hover {
    .asset-id-plate,
    .asset-id-chassis.is-primary,
    .asset-id-nickname {
      color: #c4c0fb;
    }
  }

  .virtual-fence-chip {
    background: rgba(115, 103, 240, 0.12);
    border-color: rgba(115, 103, 240, 0.3);
    color: #a8a3f8;
  }

  .additional-info-chip {
    background-color: $theme-dark-body-bg;
    border-color: $theme-dark-border-color;
    color: $theme-dark-body-color;

    &:hover {
      background-color: lighten($theme-dark-body-bg, 4%);
      border-color: #7367f0;
    }
  }

  .maps-link-btn {
    color: #a8a3f8;

    &:hover {
      color: #c4c0fb;
    }
  }

  ::v-deep .b-skeleton {
    background-color: rgba(255, 255, 255, 0.08) !important;

    &.b-skeleton-animate-wave {
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.07) 0%,
        rgba(255, 255, 255, 0.16) 50%,
        rgba(255, 255, 255, 0.07) 100%
      ) !important;
    }
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}

// ============================================
// Modal Informações Adicionais — Design Premium
// ============================================
.modal-additional-info-wrapper {
  ::v-deep .modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(115, 103, 240, 0.14) !important;
    padding: 0 !important;
  }

  ::v-deep .modal-body {
    padding: 0 !important;
    margin: 0 !important;
  }
}

.modal-info-shell {
  background: #fff;
}

.modal-info-header {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #5e50ee 0%, #7367f0 48%, #9e95f5 100%);
  padding: 1.15rem 1.25rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.modal-info-header-glow {
  position: absolute;
  right: -40px;
  top: -50px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  pointer-events: none;
}

.modal-info-header-text,
.modal-info-meta-text {
  min-width: 0;
  flex: 1;
}

.modal-info-title {
  font-size: 1rem;
  letter-spacing: 0.01em;
  line-height: 1.3;
}

.modal-info-event-badge {
  background: #fff !important;
  color: #5e50ee !important;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 2px 8px rgba(46, 38, 128, 0.18);
}

.modal-info-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.modal-info-close {
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 2px;
  }
}

.modal-info-body {
  padding: 1.15rem 1.25rem 0.5rem;
}

.modal-info-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 1rem;
}

.modal-info-meta-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  background: #f8f8fa;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
}

.modal-info-meta-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b9b9c3;
  line-height: 1.2;
}

.modal-info-meta-value {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #5e5873;
  line-height: 1.35;
}

.modal-info-content-box {
  background: linear-gradient(180deg, #f8f7ff 0%, #fbfbfe 100%);
  border: 1px solid rgba(115, 103, 240, 0.16);
  border-left: 3px solid #7367f0;
  border-radius: 0 12px 12px 0;
  padding: 0.85rem 1rem 1rem;
}

.modal-info-content-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7367f0;
}

.modal-info-text {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.7;
  color: #4a4a6a;
  word-break: break-word;
  white-space: pre-wrap;
  max-height: 220px;
  overflow-y: auto;
}

.modal-info-footer {
  display: flex;
  justify-content: flex-end;
  padding: 0.85rem 1.25rem 1.15rem;
}

.modal-info-meta-maps {
  border-color: rgba(115, 103, 240, 0.25);
  background: rgba(115, 103, 240, 0.04);
  transition: all 0.15s ease;

  &:hover {
    border-color: #7367f0;
    background: rgba(115, 103, 240, 0.08);
  }
}

.modal-info-maps-link {
  color: #7367f0;
  transition: color 0.15s ease;

  &:hover {
    color: #5e50ee;
    text-decoration: underline;
  }
}

@media (max-width: 575.98px) {
  .modal-info-meta {
    grid-template-columns: 1fr;
  }
}

.dark-layout {
  .modal-info-shell,
  .modal-info-body {
    background: #283046;
  }

  .modal-info-meta-item {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.08);
  }

  .modal-info-meta-maps {
    background: rgba(115, 103, 240, 0.1);
    border-color: rgba(115, 103, 240, 0.3);

    &:hover {
      background: rgba(115, 103, 240, 0.16);
      border-color: #7367f0;
    }
  }

  .modal-info-maps-link {
    color: #a8a3f8;

    &:hover {
      color: #c4c0fb;
    }
  }

  .modal-info-meta-value {
    color: #d0d2d6;
  }

  .modal-info-content-box {
    background: rgba(115, 103, 240, 0.08);
    border-color: rgba(115, 103, 240, 0.22);
  }

  .modal-info-text {
    color: #b4b7bd;
  }
}
</style>
